import styled from 'styled-components';

import { CARD_BORDER, CARD_SHADOW, CONTROL_BORDER, FOCUS_RING } from './base';

// ---------------------------------------------------------------------------
// Régua de Cobrança — barra de filtros (card branco): busca, segmentado de
// status, select de responsável, toggle "Só pendentes" e segmentado de visão.
// ---------------------------------------------------------------------------

export const FiltersCard = styled.section`
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${CARD_BORDER};
   border-radius: 12px;
   box-shadow: ${CARD_SHADOW};
   padding: 12px 16px;
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
`;

// Variante da barra para telas cujos controles são ROTULADOS (acerto com o
// escritório, 0103). Com label em cima, os controles têm alturas diferentes —
// o `center` da barra sem rótulo desalinharia a base dos campos.
export const LabeledFiltersCard = styled(FiltersCard)`
   align-items: flex-end;
`;

// Campo rotulado da barra: label em cima, controle embaixo. O label é o
// `FieldLabel` do kit (base.js) — não redeclarar tipografia aqui.
export const FilterField = styled.div`
   display: flex;
   flex-direction: column;
`;

export const SearchWrap = styled.div`
   position: relative;
   display: flex;
   align-items: center;
   flex: 1;
   min-width: 220px;
   max-width: 340px;

   svg {
      position: absolute;
      left: 12px;
      color: #9a9a9a;
      pointer-events: none;
   }
`;

export const SearchInput = styled.input`
   height: 40px;
   width: 100%;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 0 12px 0 38px;
   font: inherit;
   font-size: 14px;
   background: #fff;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

export const SegmentedTrack = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 4px;
   border-radius: 10px;
   background: #f4f4f5;
`;

// Item de segmentado (status e visão). Ativo = branco com sombra.
// G.4.1: hover declara background próprio.
export const SegmentedItem = styled.button`
   height: 32px;
   padding: 0 12px;
   border-radius: 8px;
   border: none;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   white-space: nowrap;
   transition: background 120ms ease, color 120ms ease;
   background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
   color: ${({ $active, theme }) =>
      $active ? theme?.colors?.textPrimary ?? '#111' : '#777'};
   box-shadow: ${({ $active }) =>
      $active ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none'};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

// Select da barra (largura própria, não 100%).
export const FilterSelect = styled.select`
   height: 40px;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 10px;
   padding: 0 10px;
   font: inherit;
   font-size: 14px;
   background: #fff;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   transition: border-color 140ms ease, box-shadow 140ms ease;

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      box-shadow: ${FOCUS_RING};
   }
`;

// Toggle "Só pendentes" — ativo fica na paleta crítica.
export const PendingToggle = styled.button`
   height: 40px;
   padding: 0 14px;
   border-radius: 10px;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   display: inline-flex;
   align-items: center;
   gap: 8px;
   transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
   border: 1px solid
      ${({ $active }) => ($active ? 'rgba(220, 38, 38, 0.4)' : CONTROL_BORDER)};
   background: ${({ $active }) =>
      $active ? 'rgba(220, 38, 38, 0.08)' : '#fff'};
   color: ${({ $active }) => ($active ? '#a01b1b' : '#555')};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? 'rgba(220, 38, 38, 0.14)' : '#f4f4f5'};
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

// Toggle de EXIBIÇÃO (ex.: "Exibir raias vazias") — mesma geometria do
// PendingToggle, mas ativo no azul de seleção (tons do ChannelChip): o
// vermelho do PendingToggle é semântica de alerta, não de "ligado".
export const DisplayToggle = styled(PendingToggle)`
   border-color: ${({ $active }) =>
      $active ? 'rgba(64, 152, 239, 0.5)' : CONTROL_BORDER};
   background: ${({ $active }) =>
      $active ? 'rgba(64, 152, 239, 0.12)' : '#fff'};
   color: ${({ $active }) => ($active ? '#0b3a63' : '#555')};

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? 'rgba(64, 152, 239, 0.18)' : '#f3f8fe'};
   }
`;

// "Pesquisar" — ação consultiva em destaque (FC7 G.1: CTA escuro, nunca o
// azul de escrita), na geometria da tela (40px / raio 10). Só habilita quando
// o rascunho difere do recorte aplicado.
export const SearchGoButton = styled.button`
   height: 40px;
   padding: 0 18px;
   border-radius: 10px;
   border: none;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   display: inline-flex;
   align-items: center;
   gap: 8px;
   background: ${({ theme }) => theme?.colors?.ctaDark ?? '#1f1f18'};
   color: #fff;
   transition: background 140ms ease;

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme?.colors?.ctaDarkHover ?? '#3a3a30'};
   }

   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

export const FiltersSpacer = styled.div`
   flex: 1;
   min-width: 8px;
`;
